import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import LazyImage from './components/LazyImage';
import SkeletonLoader from './components/SkeletonLoader';
import Spinner from './components/Spinner';
import Navigation from './components/Navigation';
import { usePerformance } from './hooks/usePerformance';
import { useIsMobile } from './hooks/useMediaQuery';
import { useMainThreadOptimization } from './hooks/useMainThreadOptimization';
import PageTitle from './components/PageTitle';

// Analytics se cargará de forma diferida
const NonBlockingAnalytics = lazy(() => import('./components/NonBlockingAnalytics'));

// Lazy loading ultra optimizado con chunks separados y preloading inteligente
// Componentes críticos (cargar inmediatamente)
const Hero = lazy(() =>
  import(/* webpackChunkName: "critical-hero" */ './Hero')
);

// Componentes de alta prioridad (cargar después del hero)
const StatsSection = lazy(() =>
  import(/* webpackChunkName: "high-priority" */ './StatsSection')
);

// Componentes de media prioridad (cargar cuando estén cerca del viewport)
const TrainingSection = lazy(() =>
  import(/* webpackChunkName: "medium-priority" */ './TrainingSection')
);
const WorkoutSection = lazy(() =>
  import(/* webpackChunkName: "medium-priority" */ './WorkoutSection')
);
const AdminSection = lazy(() =>
  import(/* webpackChunkName: "medium-priority" */ './AdminSection')
);

// Componentes de baja prioridad (cargar solo cuando sean necesarios)
const Faq = lazy(() =>
  import(/* webpackChunkName: "low-priority-sections" */ './Faq')
);
const CtaSection = lazy(() =>
  import(/* webpackChunkName: "low-priority-sections" */ './CtaSection')
);
const TrainerMaxAI = lazy(() =>
  import(/* webpackChunkName: "low-priority-sections" */ './TrainerMaxAI')
);
const PricingSection = lazy(() =>
  import(/* webpackChunkName: "low-priority-sections" */ './PricingSection')
);

// Componentes interactivos (cargar bajo demanda)
const WhatsAppButton = lazy(() =>
  import(/* webpackChunkName: "interactive-components" */ './WhatsAppButton')
);
const ContactModal = lazy(() =>
  import(/* webpackChunkName: "interactive-components" */ './ContactModal')
);

// Páginas adicionales
const OkPage = lazy(() =>
  import(/* webpackChunkName: "additional-pages" */ './OkPage')
);

// Importar PilatesLayout
const PilatesLayout = lazy(() =>
  import(/* webpackChunkName: "pilates-layout" */ './PilatesLayout')
);

const FuncionalLayout = lazy(() =>
  import(/* webpackChunkName: "pilates-layout" */ './FuncionalLayout')
);

// Importar PilatesLayout
const EntrenadorLayout = lazy(() =>
  import(/* webpackChunkName: "pilates-layout" */ './EntrenadorLayout')
);

const FisioLayout = lazy(() =>
  import(/* webpackChunkName: "pilates-layout" */ './FisioLayout')
);

const GimnasioLayout = lazy(() =>
  import(/* webpackChunkName: "pilates-layout" */ './GimnasioLayout')
);

const MainLayout = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { preloadImages } = usePerformance();
  const isMobile = useIsMobile();
  const { scheduleIdleTask } = useMainThreadOptimization();
  const [mountAnalytics, setMountAnalytics] = useState(false);

  // Preload de imágenes críticas de forma optimizada
  useEffect(() => {
    const criticalImages = [
      '/tmax.webp',
      '/dash-trainers.webp',
      '/app-1-trainers.webp'
    ];

    // Programar preload cuando el navegador esté libre
    scheduleIdleTask(() => {
      preloadImages(criticalImages);
    }, { timeout: 2000 });
  }, [preloadImages, isMobile, scheduleIdleTask]);

  // Montar analytics sólo tras interacción del usuario
  useEffect(() => {
    const trigger = () => {
      setMountAnalytics(true);
      window.removeEventListener('click', trigger);
      window.removeEventListener('keydown', trigger);
      window.removeEventListener('scroll', triggerOnceOnDeepScroll, { passive: true });
    };
    const triggerOnceOnDeepScroll = () => {
      if (window.scrollY > 300) trigger();
    };
    window.addEventListener('click', trigger, { once: true });
    window.addEventListener('keydown', trigger, { once: true });
    window.addEventListener('scroll', triggerOnceOnDeepScroll, { passive: true });
    return () => {
      window.removeEventListener('click', trigger);
      window.removeEventListener('keydown', trigger);
      window.removeEventListener('scroll', triggerOnceOnDeepScroll);
    };
  }, []);

  // Eliminado el prefetch de componentes para diferir chunks hasta que se necesiten

  const faqData = [
    {
      q: '¿Qué es Trainer Max?',
      a: 'Es una plataforma 100% para entrenadores personales. Permite crear planes de entreamiento personalizados con videos de Trainer Max o tus propios videos y que tus clientes lo vean desde una aplicación movil.'
    },
    {
      q: '¿Por Qué Trainer Max y no otra plataforma?',
      a: 'Todas nuestras funcionalidades se basan en el feedback obtenido por entrenadores. No cobramos precios exorbitantes, cobramos el precio justo.'
    },
    {
      q: '¿Es difícil de implementar?',
      a: 'El alta es instantanea junto a una prueba gratis de 7 días.'
    },
    {
      q: '¿De donde son?',
      a: 'Somos una empresa que trabaja con centros  entrenadores de todo el mundo y nuestra sede principal esta en Argentina.'
    },
    {
      q: '¿Qué incluye?',
      a: 'Editor de planes de entrenamiento personalizado con diferentes modalidades. Aplicación para tus clientes. Sisteam de gestión para medir la evolución de tus alumnos y métricas.'
    },
    {
      q: '¿Como funciona la app para mis clientes?',
      a: 'La aplicación Trainer Max, que puedes encontrar en Play Store y App Store, la utilizan tus clientes para ver entrenamientos, pagar membresías y enterarse de todas las novedades que cargas. Puedes personalizar los colores principales que aparecen en toda la app, las fotos de las diferentes secciones, que secciones aparecen y cuales no y el logo de tu marca que aparece dentro de la app. Por otro lado la app custom, es identica, pero tiene como adicional la posibilidad de que tus clientes la busquen en Play Store y App Store con tu marca de forma directa, no se comparte con otros entrenadores. '
    },
    {
      q: '¿Hay prueba gratis?',
      a: 'Puedes solicitar una prueba gratis por 7 días y una videollamada con nuestros asesores para evacuar cualquier consulta que tengas.'
    },
    {
      q: '¿Me pueden mostrar todo desde una videollamada?',
      a: 'Si, puedes ver todo desde una videollamada con nuestros asesores y luego decidir si avanzas con la contratación.'
    }
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <Navigation openContactModal={() => setIsContactModalOpen(true)} />

      <main>
        <Suspense
          fallback={<SkeletonLoader type="hero" />}>
          {/* Hero Section */}
          <div id="hero">
            <Hero openContactModal={() => setIsContactModalOpen(true)} />
          </div>

          {/* Stats Section */}
          <StatsSection />
        </Suspense>


        {/* Funcionalidades Section */}
        <section id="funcionalidades" className="funcionalidades-header">
          <div className="container">
            <h2 className="funcionalidades-title">¿Cómo podemos ayudarte?</h2>
            <p className="funcionalidades-subtitle">Trabajamos constantemente con entrenadores personales</p>
          </div>
        </section>

        <Suspense fallback={<SkeletonLoader type="section" />}>
          {/* Additional Feature Sections */}

          <WorkoutSection openContactModal={() => setIsContactModalOpen(true)} />
          <AdminSection openContactModal={() => setIsContactModalOpen(true)} />
          <TrainingSection openContactModal={() => setIsContactModalOpen(true)} />
        </Suspense>


        <Suspense fallback={<SkeletonLoader type="default" lines={5} />}>
          {/* CTA Section */}
          <CtaSection openContactModal={() => setIsContactModalOpen(true)} />

          {/* Trainer Max AI Section */}
          <div id="trainer-max-ai">
            <TrainerMaxAI openContactModal={() => setIsContactModalOpen(true)} />
          </div>

          {/* Testimonials - Comentado temporalmente */}
          {/* <div id="testimonials">
            <Testimonials />
          </div> */}

          {/* FAQ Section */}
          <div id="faq">
            <Faq faqs={faqData} />
          </div>

          {/* Pricing Section */}
          <div id="precios">
            <PricingSection openContactModal={() => setIsContactModalOpen(true)} />
          </div>

          <CtaSection openContactModal={() => setIsContactModalOpen(true)} />
        </Suspense>
      </main>

      {/* Footer */}
      <footer id="contacto" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <LazyImage src="/tmax.webp" alt="Trainer Max" className="footer-logo" />
            </div>
            <div className="footer-social">
              <a href="https://www.facebook.com/trainermax.software" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v2.385z" /></svg>
              </a>
              <a href="https://www.instagram.com/trainermax.app" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85s-.012 3.584-.07 4.85c-.148 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069s-3.584-.012-4.85-.07c-3.252-.148-4.771-1.691-4.919-4.919-.058-1.265-.069-1.645-.069-4.85s.012-3.584.07-4.85c.148-3.225 1.664 4.771 4.919-4.919 1.266-.057 1.644-.069 4.85-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948s.014 3.667.072 4.947c.2 4.359 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072s3.667-.014 4.947-.072c4.359-.2 6.78-2.618 6.98-6.98.058-1.281.072-1.689.072-4.948s-.014-3.667-.072-4.947c-.2-4.359-2.618-6.78-6.98-6.98-1.281-.059-1.689-.073-4.948-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.162 6.162 6.162 6.162-2.759 6.162-6.162-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4s1.791-4 4-4 4 1.79 4 4-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.441 1.441 1.441 1.441-.645 1.441-1.441-.645-1.44-1.441-1.44z" /></svg>
              </a>

            </div>
          </div>
          <div className="footer-bottom">
            <p>©{new Date().getFullYear()} Trainer Max - Software de Gestión para Entrenadores</p>
            <div className="footer-legal">
              <a href="/pp" target="_blank" rel="noopener noreferrer">Política de privacidad</a>
              <a href="/tyc" target="_blank" rel="noopener noreferrer">Términos y condiciones</a>
            </div>
          </div>
        </div>
      </footer>

      <Suspense fallback={<Spinner size="small" color="success" />}>
        {/* WhatsApp Button */}
        <WhatsAppButton />

        {/* Contact Modal */}
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        />
      </Suspense>

      {/* Analytics no bloqueante (carga diferida) */}
      {mountAnalytics && (
        <Suspense fallback={null}>
          <NonBlockingAnalytics />
        </Suspense>
      )}
    </div>
  );
}

const PdfViewer = () => {
  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <iframe
        src="/documentos/yppweb1.pdf"
        title="Política de privacidad"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const PPS = ({ id }) => {
  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <iframe
        src={`/documentos/pp/${id}.pdf`}
        title="Política de privacidad"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/ok" element={<OkPage />} />
      <Route path="/pp" element={<PdfViewer />} />
      <Route path="/software-para-estudios-de-pilates-y-yoga" element={<><PageTitle title="Software para Estudios de Pilates y Yoga | Trainer Max" /><PilatesLayout /></>} />
      <Route path="/software-para-estudios-de-funcional-y-cross" element={<><PageTitle title="Software para Estudios de Funcional y Cross | Trainer Max" /><FuncionalLayout /></>} />
      <Route path="/software-para-personal-trainer" element={<><PageTitle title="Software para Personal Trainers | Trainer Max" /><EntrenadorLayout /></>} />
      <Route path="/software-para-kinesiologos-fisioterapeutas" element={<><PageTitle title="Software para Kinesiólogos y Fisioterapeutas | Trainer Max" /><FisioLayout /></>} />
      <Route path="/software-para-gimnasios" element={<><PageTitle title="Software para Gimnasios | Trainer Max" /><GimnasioLayout /></>} />
      <Route path="/*" element={<MainLayout />} />
      <Route path="/pp65f0ebba445029fc64382d4f" element={<PPS id="65f0ebba445029fc64382d4f" />} />
      <Route path="/pp66b7e930ae185546ae99d1a6" element={<PPS id="66b7e930ae185546ae99d1a6" />} />
      <Route path="/pp66b78a074d6f4144d36fe69f" element={<PPS id="66b78a074d6f4144d36fe69f" />} />
      <Route path="/pp67f322e6605e817e97deefca" element={<PPS id="67f322e6605e817e97deefca" />} />
      <Route path="/pp666dd620aaa4321c2ca7f037" element={<PPS id="666dd620aaa4321c2ca7f037" />} />
      <Route path="/pp683f4cddb1b824fbfca8b53f" element={<PPS id="683f4cddb1b824fbfca8b53f" />} />
      <Route path="/pp66844ae47948d41641794811" element={<PPS id="66844ae47948d41641794811" />} />
      <Route path="/pp67291ebfc1482b5153516b20" element={<PPS id="67291ebfc1482b5153516b20" />} />
      <Route path="/pp66facd98cb324fba86a72622" element={<PPS id="66facd98cb324fba86a72622" />} />
      <Route path="/pp669fa9f83d4da8b076cb541b" element={<PPS id="669fa9f83d4da8b076cb541b" />} />
      <Route path="/pp69a1fc5a6125491f67965119" element={<PPS id="69a1fc5a6125491f67965119" />} />


      <Route path="/ppApp1" element={<PPS id="App1" />} />
    </Routes>
  );
}

export default App;
